import { JrModel } from './config'
import type { SessionCosts, TurnCost } from './types'

// JR GenAI gateway cost formula: (inputCredits + outputCredits) * 0.0000165 = USD
const CREDIT_TO_USD = 0.0000165

// Tokens-per-credit factors by model alias.
// Source: gen-ai-service/src/config.ts + rateLimiter.ts (BASE_PRICE_PER_1M_TOKEN_IN_USD = 16.5)
// Factor = BASE_PRICE / AWS price per 1M tokens. Inverse of gateway's credit = token / factor.
const MODEL_CREDIT_FACTORS: Record<string, { input: number; output: number }> =
  {
    [JrModel.Standard]: { input: 5, output: 1 },
    [JrModel.Fast]: { input: 15, output: 3 },
    [JrModel.Powerful]: { input: 3, output: 0.6 },
  }

export function creditsToTokens(
  credits: number,
  model: string,
  dir: 'input' | 'output',
): number {
  return Math.round(credits * (MODEL_CREDIT_FACTORS[model]?.[dir] ?? 1))
}

export function calcSessionActualTokens(costs: SessionCosts): {
  input: number
  output: number
} {
  let input = 0
  let output = 0
  for (const [model, usage] of Object.entries(costs.perModelUsage)) {
    input += creditsToTokens(usage.inputCredits, model, 'input')
    output += creditsToTokens(usage.outputCredits, model, 'output')
  }
  return { input, output }
}

export function calcCostUsd(turn: TurnCost): number {
  return (turn.inputCredits + turn.outputCredits) * CREDIT_TO_USD
}

export function calcMessageUsageCostUsd(usage: {
  model: string
  inputTokens: number
  outputTokens: number
}): number {
  const factors = MODEL_CREDIT_FACTORS[usage.model]
  if (!factors) return 0
  const inputCredits = usage.inputTokens / factors.input
  const outputCredits = usage.outputTokens / factors.output
  return (inputCredits + outputCredits) * CREDIT_TO_USD
}

export function calcSessionCostUsd(costs: SessionCosts): number {
  return (costs.totalInputCredits + costs.totalOutputCredits) * CREDIT_TO_USD
}

export function createSessionCosts(): SessionCosts {
  return {
    turns: [],
    totalInputCredits: 0,
    totalOutputCredits: 0,
    totalCacheReadInputCredits: 0,
    totalCacheCreationInputCredits: 0,
    byModel: {},
    perModelUsage: {},
  }
}

export interface TurnCostInput {
  stage: TurnCost['stage']
  model: string
  inputTokens: number
  outputTokens: number
  cacheReadInputTokens: number
  cacheCreationInputTokens: number
  durationMs: number
}

export function recordTurnCost(
  costs: SessionCosts,
  input: TurnCostInput,
): TurnCost {
  const turn: TurnCost = {
    stage: input.stage,
    model: input.model,
    inputCredits: Math.round(input.inputTokens),
    outputCredits: Math.round(input.outputTokens),
    cacheReadInputCredits: Math.round(input.cacheReadInputTokens),
    cacheCreationInputCredits: Math.round(input.cacheCreationInputTokens),
    durationMs: input.durationMs,
  }

  costs.turns.push(turn)
  costs.totalInputCredits += turn.inputCredits
  costs.totalOutputCredits += turn.outputCredits
  costs.totalCacheReadInputCredits += turn.cacheReadInputCredits
  costs.totalCacheCreationInputCredits += turn.cacheCreationInputCredits

  const m = (costs.byModel[turn.model] ??= { input: 0, output: 0 })
  m.input += turn.inputCredits
  m.output += turn.outputCredits

  const existing = costs.perModelUsage[turn.model]
  if (existing) {
    existing.inputCredits += turn.inputCredits
    existing.outputCredits += turn.outputCredits
    existing.cacheReadInputCredits += turn.cacheReadInputCredits
    existing.cacheCreationInputCredits += turn.cacheCreationInputCredits
    existing.durationMs += turn.durationMs
  } else {
    costs.perModelUsage[turn.model] = { ...turn }
  }

  return turn
}
